import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useLanguage } from "../../context/LanguageContext";
import { useAlert } from "../../Hooks/useAlert";
import homeLanguage from "../../language/homeLanguage";
import reportLanguage from "../../language/reportLanguage";
import { customerAPI, dailyMilkAPI, reportAPI, customerLeaveAPI, customerAssignmentAPI, authAPI } from "../../services/api";
import Alert from "../common/Alert";
import Spinner from "../common/Spinner";

// Helper: format Date -> "24 July, 2024"
function formatDisplayDate(date) {
  if (!date) return "";
  const d = new Date(date);
  const day = d.getDate().toString().padStart(2, "0");
  const month = d.toLocaleString("en-US", { month: "long" });
  const year = d.getFullYear();
  return `${day} ${month}, ${year}`;
}

// Helper: convert Date -> "yyyy-MM-dd" for input value & max
function toInputDateValue(date) {
  if (!date) return "";
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function HomePage() {
  const { language } = useLanguage();
  const text = homeLanguage[language];
  const { showAlert, alertConfig } = useAlert();

  const dateInputRef = useRef(null);
  const [dateFilter, setDateFilter] = useState("today");
  const [selectedDate, setSelectedDate] = useState("");
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [search, setSearch] = useState("");
  const [customers, setCustomers] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [loading, setLoading] = useState(false);
  const [isHoliday, setIsHoliday] = useState(false);
  const [savedQuantities, setSavedQuantities] = useState({});
  const [onLeaveCustomerIds, setOnLeaveCustomerIds] = useState([]);

  // Popup state
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [popupMonthOffset, setPopupMonthOffset] = useState(0);
  const [customerEntries, setCustomerEntries] = useState([]);
  const [popupLoading, setPopupLoading] = useState(false);
  const [editingRowIdx, setEditingRowIdx] = useState(null);
  const [editValues, setEditValues] = useState({ quantityLiters: "", milkRate: "" });
  const [savingRowIdx, setSavingRowIdx] = useState(null);

  const popupText = reportLanguage[language];

  const today = useMemo(() => new Date(), []);
  const yesterday = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d;
  }, []);

  const effectiveDate = useMemo(() => {
    if (dateFilter === "today") return today;
    if (dateFilter === "yesterday") return yesterday;
    if (dateFilter === "custom" && selectedDate) return new Date(selectedDate);
    return today;
  }, [dateFilter, today, yesterday, selectedDate]);

  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    if (customers.length > 0) {
      loadDailyEntries();
    }
  }, [effectiveDate, customers.length]);

  const loadCustomers = async () => {
    try {
      setLoading(true);

      const currentUser = authAPI.getCurrentUser();
      const isEmployee = currentUser?.role === "Employee";
      let customerData;

      if (isEmployee) {
        const empResp = await customerAssignmentAPI.getMyCustomers(currentUser.employeeId);
        if (empResp.success && Array.isArray(empResp.data)) {
          customerData = empResp.data.map((c) => ({
            id: c.customerId,
            name: c.customerName,
            milkType: c.milkType === 0 ? "cow" : c.milkType === 1 ? "buffalo" : "both",
            defaultQty: c.milkType === 0
              ? (c.cowDefaultLiters || 1)
              : (c.buffaloDefaultLiters || 0.5),
            step: c.milkType === 0 ? 1 : 0.5,
          }));
        }
      } else {
        const response = await customerAPI.getCustomers();
        if (response.success) {
          customerData = response.data.map((c) => ({
            id: c.customerId,
            name: c.customerName,
            milkType:
              c.milkType === 0 ? "cow" : c.milkType === 1 ? "buffalo" : "both",
            defaultQty:
              c.milkType === 0
                ? c.cowDefaultLiters || 1
                : c.buffaloDefaultLiters || 0.5,
            step: c.milkType === 0 ? 1 : 0.5,
          }));
        }
      }

      if (customerData) {
        setCustomers(customerData);
        const initialQty = {};
        customerData.forEach((c) => {
          initialQty[c.id] = c.defaultQty;
        });
        setQuantities(initialQty);
      }
    } catch (error) {
      console.error("Error loading customers:", error);
      await showAlert({
        type: "error",
        title: "Error",
        message: "Failed to load customers. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadDailyEntries = async () => {
    try {
      const dateStr = toInputDateValue(effectiveDate);

      const [entryResponse, leaveResponse] = await Promise.all([
        dailyMilkAPI.getDailyEntries(dateStr),
        customerLeaveAPI.getLeavesByDate(dateStr),
      ]);

      // Determine which customers are on leave for this date
      let leaveCustomerIds = [];
      if (leaveResponse.success && Array.isArray(leaveResponse.data)) {
        leaveCustomerIds = leaveResponse.data;
      }
      setOnLeaveCustomerIds(leaveCustomerIds);

      if (entryResponse.success && entryResponse.data) {
        const { entries, isHoliday: holidayFlag } = entryResponse.data;

        const newQty = {};
        customers.forEach((c) => {
          newQty[c.id] = c.defaultQty;
        });

        if (entries && entries.length > 0) {
          entries.forEach((entry) => {
            newQty[entry.customerId] = entry.quantity;
          });
        }

        // Override with 0 for customers on leave
        leaveCustomerIds.forEach((id) => {
          newQty[id] = 0;
        });

        setQuantities(newQty);
        setIsHoliday(holidayFlag || false);
      } else {
        const defaultQty = {};
        customers.forEach((c) => {
          defaultQty[c.id] = leaveCustomerIds.includes(c.id) ? 0 : c.defaultQty;
        });
        setQuantities(defaultQty);
        setIsHoliday(false);
      }
    } catch (error) {
      console.error("Error loading daily entries:", error);
    }
  };

  const filteredCustomers = customers.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleIncrement = (id, step) => {
    setQuantities((prev) => ({
      ...prev,
      [id]: Number((prev[id] + step).toFixed(2)),
    }));
  };

  const handleDecrement = (id, step) => {
    setQuantities((prev) => ({
      ...prev,
      [id]: Math.max(0, Number((prev[id] - step).toFixed(2))),
    }));
  };

  const handleCustomDateChange = async (e) => {
    const value = e.target.value;
    if (!value) {
      setSelectedDate("");
      setShowCustomPicker(false);
      return;
    }

    const picked = new Date(value);
    const todayOnly = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    const pickedOnly = new Date(
      picked.getFullYear(),
      picked.getMonth(),
      picked.getDate()
    );

    if (pickedOnly.getTime() > todayOnly.getTime()) {
      await showAlert({
        type: "warning",
        title: "Invalid Date",
        message: "You cannot select a future date.",
      });
      return;
    }

    setSelectedDate(value);
    setShowCustomPicker(false);
  };

  const handleDateFilterClick = (type) => {
    setDateFilter(type);
    if (type === "custom") {
      setShowCustomPicker(true);
      // Auto-trigger calendar picker after state update
      setTimeout(() => {
        if (dateInputRef.current) {
          dateInputRef.current.showPicker?.();
        }
      }, 100);
    } else {
      setShowCustomPicker(false);
    }
  };

  const handleHolidayToggle = () => {
    if (!isHoliday) {
      setSavedQuantities({ ...quantities });
      const zeroQty = {};
      customers.forEach((c) => {
        zeroQty[c.id] = 0;
      });
      setQuantities(zeroQty);
      setIsHoliday(true);
    } else {
      if (Object.keys(savedQuantities).length > 0) {
        setQuantities({ ...savedQuantities });
      } else {
        const defaultQty = {};
        customers.forEach((c) => {
          defaultQty[c.id] = c.defaultQty;
        });
        setQuantities(defaultQty);
      }
      setIsHoliday(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      const entries = customers.map((c) => ({
        customerId: c.id,
        milkType: c.milkType === "cow" ? 0 : 1,
        quantity: quantities[c.id] || 0,
      }));

      const dateStr = toInputDateValue(effectiveDate);
      const response = await dailyMilkAPI.saveDailyEntries(
        dateStr,
        entries,
        isHoliday
      );

      if (response.success) {
        await showAlert({
          type: "success",
          title: "Success",
          message: "Daily entries saved successfully!",
        });
      } else {
        await showAlert({
          type: "error",
          title: "Error",
          message: "Failed to save entries. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error saving entries:", error);
      await showAlert({
        type: "error",
        title: "Error",
        message: "Error saving entries. Please check your connection.",
      });
    } finally {
      setLoading(false);
    }
  };

  // --- Popup Helpers (same as Report.js) ---
  const getPopupMonthYear = useCallback((offset) => {
    const d = new Date();
    d.setDate(1); // prevent day-overflow when current day > last day of target month
    d.setMonth(d.getMonth() + offset);
    return { year: d.getFullYear(), month: d.getMonth() + 1, date: d };
  }, []);

  const getMonthLabel = (offset) => {
    const { date } = getPopupMonthYear(offset);
    return date.toLocaleString("en-US", { month: "long", year: "numeric" });
  };

  const loadCustomerEntries = useCallback(async (customerId, offset) => {
    setPopupLoading(true);
    setCustomerEntries([]);
    setEditingRowIdx(null);
    try {
      const { year, month } = getPopupMonthYear(offset);
      const response = await reportAPI.getCustomerMonthlyEntries(customerId, year, month);
      if (response && response.success) {
        setCustomerEntries(response.data || []);
      } else {
        setCustomerEntries([]);
      }
    } catch (error) {
      console.error("Error loading customer entries:", error);
      setCustomerEntries([]);
    } finally {
      setPopupLoading(false);
    }
  }, [getPopupMonthYear]);

  const handleOpenPopup = (customer) => {
    setSelectedCustomer(customer);
    setPopupMonthOffset(0);
    loadCustomerEntries(customer.id, 0);
  };

  const handleClosePopup = () => {
    setSelectedCustomer(null);
    setCustomerEntries([]);
    setEditingRowIdx(null);
    setPopupMonthOffset(0);
  };

  const handlePopupMonthChange = (offset) => {
    setPopupMonthOffset(offset);
    loadCustomerEntries(selectedCustomer.id, offset);
  };

  const handleEditRow = (idx, entry) => {
    setEditingRowIdx(idx);
    setEditValues({
      quantityLiters: entry.quantityLiters ?? "",
      milkRate: entry.milkRate ?? "",
    });
  };

  const handleCancelEdit = () => {
    setEditingRowIdx(null);
    setEditValues({ quantityLiters: "", milkRate: "" });
  };

  const handleSaveRow = async (idx) => {
    const entry = customerEntries[idx];
    setSavingRowIdx(idx);
    try {
      const response = await reportAPI.updateCustomerDailyEntry(
        entry.entryId,
        parseFloat(editValues.quantityLiters) || 0,
        parseFloat(editValues.milkRate) || 0,
      );
      if (response && response.success) {
        const updated = [...customerEntries];
        updated[idx] = {
          ...entry,
          quantityLiters: parseFloat(editValues.quantityLiters) || 0,
          milkRate: parseFloat(editValues.milkRate) || 0,
        };
        setCustomerEntries(updated);
        setEditingRowIdx(null);
      } else {
        await showAlert({ type: "error", title: "Error", message: response?.message || "Failed to save entry." });
      }
    } catch (error) {
      await showAlert({ type: "error", title: "Error", message: "Failed to save entry." });
    } finally {
      setSavingRowIdx(null);
    }
  };

  const headingDateText = formatDisplayDate(effectiveDate);

  return (
    <>
      <header className="sticky top-0 z-10 bg-background-light p-4 pb-2 dark:bg-background-dark md:static md:p-6 md:pb-3">
        <div className="flex items-center justify-between md:hidden">
          <div className="flex items-center gap-2 ">
            <img className="logo" src="/images/logo.png" alt={text.appTitle} />
          </div>
          <h1 className="text-lg font-bold text-slate-900 dark:text-white">
            {text.appTitle}
          </h1>
          <button
            onClick={handleSave}
            disabled={loading}
            className="flex items-center justify-center gap-1 px-3 py-2 bg-primary rounded-lg text-white font-bold text-sm md:hidden disabled:opacity-50"
          >
            <i className="fas fa-save" />
            <span>{loading ? "Saving..." : text.saveButton}</span>
          </button>
        </div>

        <div className="mt-0 md:mt-0 flex items-center gap-3">
          <div className="relative flex-1">
            <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="h-12 w-full rounded-xl border border-slate-300 bg-white pl-10 pr-4 text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-white focus:outline-none focus:ring-0 focus:border-primary"
              placeholder={text.searchCustomersPlaceholder}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <button
            onClick={handleSave}
            disabled={loading}
            className="hidden md:flex items-center justify-center gap-1 px-3 py-2 bg-primary rounded-lg text-white font-bold text-sm disabled:opacity-50"
          >
            <i className="fas fa-save" />
            <span>{loading ? "Saving..." : text.saveButton}</span>
          </button>
        </div>

        <div className="mt-2 md:mt-2 flex flex-col gap-2">
          <div className="flex gap-2">
            <button
              onClick={() => handleDateFilterClick("today")}
              className={`flex h-10 flex-1 items-center justify-center rounded-lg text-sm ${
                dateFilter === "today"
                  ? "bg-primary text-white font-bold"
                  : "bg-primary/20 text-primary"
              }`}
            >
              {text.today}
            </button>
            <button
              onClick={() => handleDateFilterClick("yesterday")}
              className={`flex h-10 flex-1 items-center justify-center rounded-lg text-sm ${
                dateFilter === "yesterday"
                  ? "bg-primary text-white font-bold"
                  : "bg-primary/20 text-primary dark:bg-primary/30"
              }`}
            >
              {text.yesterday}
            </button>
            <button
              onClick={() => handleDateFilterClick("custom")}
              className={`flex h-10 flex-1 items-center justify-center rounded-lg text-sm ${
                dateFilter === "custom"
                  ? "bg-primary text-white font-bold"
                  : "bg-primary/20 text-primary dark:bg-primary/30"
              }`}
            >
              {text.customDate}
            </button>
          </div>

          {showCustomPicker && (
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-4 border border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300 whitespace-nowrap">
                  {text.customDate}
                </label>
                <input
                  ref={dateInputRef}
                  type="date"
                  className="h-10 flex-1 rounded-lg border border-slate-300 dark:border-slate-600 px-3 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-0 focus:border-primary"
                  value={selectedDate || ""}
                  max={toInputDateValue(today)}
                  onChange={handleCustomDateChange}
                  onClick={(e) => e.target.showPicker?.()}
                  autoFocus
                />
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-4 pt-0 pb-24 md:p-6 md:pt-0 md:pb-6">
        <section>
          <div className="mb-3 flex items-center justify-between gap-2">
            <h2 className="text-base md:text-md font-bold text-slate-800 dark:text-white">
              {text.dailyEntriesFor} {headingDateText}
            </h2>

            <button
              onClick={handleHolidayToggle}
              className={`flex items-center justify-center w-10 h-10 md:w-11 md:h-11 rounded-full transition-all duration-300 shadow-md hover:shadow-lg ${
                isHoliday
                  ? "bg-orange-500 hover:bg-orange-600"
                  : "bg-green-500 hover:bg-green-600"
              }`}
              title={isHoliday ? "Exit Holiday Mode" : "Enter Holiday Mode"}
            >
              <i
                className={`fas ${
                  isHoliday ? "fa-umbrella-beach" : "fa-calendar-check"
                } text-white text-lg`}
              />
            </button>
          </div>

          {loading ? (
            <Spinner />
          ) : (
            <div className="grid gap-3 md:grid-cols-1 lg:grid-cols-2">
              {filteredCustomers.map((c) => {
                const isOnLeave = onLeaveCustomerIds.includes(c.id);
                return (
                <div
                  key={c.id}
                  className={`customer-card bg-white dark:bg-slate-800/60 rounded-xl shadow-md p-2 md:p-3 transition-all duration-300 cursor-pointer hover:shadow-lg ${
                    isHoliday ? "opacity-60 pointer-events-none" : ""
                  }`}
                  onClick={() => handleOpenPopup(c)}
                >
                  <div className="flex items-center justify-between mb-0">
                    <p className="font-bold text-slate-900 dark:text-white text-sm md:text-base">
                      {c.name}
                      {isOnLeave && (
                        <span className="ml-2 text-xs bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-200 px-1.5 py-0.5 rounded-full font-semibold">
                          On Leave
                        </span>
                      )}
                    </p>
                    <div className="flex items-center gap-1.5 md:gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDecrement(c.id, c.step); }}
                        className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/20 text-primary"
                        disabled={isHoliday}
                      >
                        <i className="fas fa-minus text-xs" />
                      </button>
                      <input
                        className="h-8 w-12 md:h-9 md:w-14 rounded-lg border border-slate-300 bg-white text-center font-bold text-slate-900 dark:border-slate-600 dark:bg-slate-700 dark:text-white text-xs md:text-sm focus:outline-none focus:ring-0 focus:border-primary"
                        type="number"
                        step={c.step}
                        value={quantities[c.id] === 0 ? "" : quantities[c.id]}
                        onChange={(e) =>
                          setQuantities((prev) => ({
                            ...prev,
                            [c.id]: e.target.value === "" ? "" : parseFloat(e.target.value),
                          }))
                        }
                        onBlur={(e) =>
                          setQuantities((prev) => ({
                            ...prev,
                            [c.id]: e.target.value === "" ? 0 : parseFloat(e.target.value) || 0,
                          }))
                        }
                        onClick={(e) => e.stopPropagation()}
                        disabled={isHoliday}
                      />
                      <button
                        onClick={(e) => { e.stopPropagation(); handleIncrement(c.id, c.step); }}
                        className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/20 text-primary"
                        disabled={isHoliday}
                      >
                        <i className="fas fa-plus text-xs" />
                      </button>
                    </div>
                  </div>

                  <div className="customer-info-line flex items-center gap-2 flex-wrap">
                    <span
                      className={`milk-type-badge ${
                        c.milkType === "cow"
                          ? "milk-type-cow"
                          : "milk-type-buffalo"
                      }`}
                    >
                      {c.milkType === "cow"
                        ? text.milkTypeCow
                        : text.milkTypeBuffalo}
                    </span>
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      {text.defaultLabel}: {c.defaultQty}
                      {text.literUnit}
                    </span>
                  </div>
                </div>
              );
              })}
            </div>
          )}
        </section>
      </main>

      {/* Customer Entries Popup */}
      {selectedCustomer && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={handleClosePopup}
        >
          <div
            className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Popup Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700 bg-primary rounded-t-2xl">
              <h2 className="text-lg font-bold text-white truncate">
                {selectedCustomer.name}
              </h2>
              <button
                onClick={handleClosePopup}
                className="text-white hover:text-slate-200 transition ml-3 flex-shrink-0"
              >
                <i className="fas fa-times text-xl" />
              </button>
            </div>

            {/* Month Selector Buttons */}
            <div className="flex gap-3 px-5 py-3 border-b border-slate-200 dark:border-slate-700">
              <button
                onClick={() => handlePopupMonthChange(0)}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition ${
                  popupMonthOffset === 0
                    ? "bg-primary text-white"
                    : "bg-primary/20 text-primary dark:bg-primary/30"
                }`}
              >
                {popupText.currentMonth}
                <span className="block text-xs font-normal opacity-80">{getMonthLabel(0)}</span>
              </button>
              <button
                onClick={() => handlePopupMonthChange(-1)}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition ${
                  popupMonthOffset === -1
                    ? "bg-primary text-white"
                    : "bg-primary/20 text-primary dark:bg-primary/30"
                }`}
              >
                {popupText.previousMonth}
                <span className="block text-xs font-normal opacity-80">{getMonthLabel(-1)}</span>
              </button>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-y-auto px-5 py-3">
              {popupLoading ? (
                <div className="flex justify-center py-10">
                  <Spinner />
                </div>
              ) : customerEntries.length === 0 ? (
                <div className="text-center py-10 text-slate-500 dark:text-slate-400">
                  <i className="fas fa-calendar-times text-4xl mb-3 block" />
                  <p className="text-sm">{popupText.noEntries}</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-sm uppercase text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                      <th className="py-2 text-left">{popupText.date}</th>
                      <th className="py-2 text-center">{popupText.liter}</th>
                      <th className="py-2 text-center">{popupText.milkType}</th>
                      <th className="py-2 text-center">{popupText.rate} (₹)</th>
                      <th className="py-2 text-center">{popupText.action}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerEntries.map((entry, idx) => {
                      const isEditing = editingRowIdx === idx;
                      const isSaving = savingRowIdx === idx;
                      const entryDate = new Date(entry.entryDate);
                      const dateStr = entryDate.toLocaleDateString("en-IN", {
                        day: "2-digit", month: "short"
                      });
                      return (
                        <tr key={idx} className={`border-b border-slate-100 dark:border-slate-700 ${isEditing ? "bg-primary/5" : "hover:bg-slate-50 dark:hover:bg-slate-700/40"}`}>
                          <td className="py-2.5 text-slate-700 dark:text-slate-300 font-medium">{dateStr}</td>
                          <td className="py-2.5 text-center">
                            {isEditing ? (
                              <input
                                type="number"
                                step="0.1"
                                className="w-16 border border-primary rounded px-1.5 py-1 text-center text-sm focus:outline-none focus:ring-1 focus:ring-primary dark:bg-slate-700 dark:text-white"
                                value={editValues.quantityLiters}
                                onChange={(e) => setEditValues(v => ({ ...v, quantityLiters: e.target.value }))}
                              />
                            ) : (
                              <span className="font-semibold">{entry.quantityLiters?.toFixed(1)} L</span>
                            )}
                          </td>
                          <td className="py-2.5 text-center">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                              entry.milkType === 0 || entry.milkType === "cow"
                                ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100"
                                : "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100"
                            }`}>
                              {entry.milkType === 0 || entry.milkType === "cow" ? popupText.cow : popupText.buffalo}
                            </span>
                          </td>
                          <td className="py-2.5 text-center">
                            {isEditing ? (
                              <input
                                type="number"
                                step="0.01"
                                className="w-16 border border-primary rounded px-1.5 py-1 text-center text-sm focus:outline-none focus:ring-1 focus:ring-primary dark:bg-slate-700 dark:text-white"
                                value={editValues.milkRate}
                                onChange={(e) => setEditValues(v => ({ ...v, milkRate: e.target.value }))}
                              />
                            ) : (
                              <span>₹{entry.milkRate?.toFixed(0)}</span>
                            )}
                          </td>
                          <td className="py-2.5 text-center">
                            {isEditing ? (
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  onClick={() => handleSaveRow(idx)}
                                  disabled={isSaving}
                                  className="px-2.5 py-1 bg-green-500 hover:bg-green-600 text-white rounded text-xs font-semibold disabled:opacity-50 transition"
                                >
                                  {isSaving ? <span className="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-white" /> : popupText.save}
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  className="px-2.5 py-1 bg-slate-400 hover:bg-slate-500 text-white rounded text-xs font-semibold transition"
                                >
                                  {popupText.cancel}
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleEditRow(idx, entry)}
                                className="text-primary hover:text-[#007aa3] transition"
                                title={popupText.edit}
                              >
                                <i className="fas fa-edit" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Popup Footer */}
            <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <span className="text-sm text-slate-500 dark:text-slate-400">
                {customerEntries.length} {popupText.entries}
              </span>
              <button
                onClick={handleClosePopup}
                className="px-5 py-2 bg-primary hover:bg-[#007aa3] text-white rounded-lg text-sm font-semibold transition"
              >
                {popupText.close}
              </button>
            </div>
          </div>
        </div>
      )}

      {alertConfig && <Alert {...alertConfig} />}
    </>
  );
}

export default HomePage;
